import { getLocales, Locale } from 'expo-localization';

export type SupportedLanguageCode = 'en' | 'es';

/**
 * Narrow an arbitrary locale string to a language we support, falling back to English.
 *
 * Exists for the locale a **host** hands `OnboardingRoot`: it speaks whatever its own UI
 * speaks (`es-MX`, `es_US`, `es`), and requiring it to know our two-letter union would leak
 * our internals into its call site. Splits on both separators because BCP-47 uses `-` and
 * Transifex-style codes use `_`.
 */
export const toSupportedLanguageCode = (locale: string): SupportedLanguageCode =>
  locale.split(/[-_]/)[0]?.toLowerCase() === 'es' ? 'es' : 'en';

/**
 * Gets the system locale from the browser or OS/app
 * @returns The system locale code (e.g., 'en-US', 'es-US', 'es')
 */
export const getSystemLocale = (): SupportedLanguageCode => {
  const locales = getLocales();
  return getPreferredSupportedLocale(locales);
};

/**
 * Gets the first supported locale from the list of locales
 * @param locales - The list of locales in order of user preference
 * @returns The preferred supported locale code, fallback to 'en' if no supported locale is found
 */
export const getPreferredSupportedLocale = (locales: Locale[]): SupportedLanguageCode => {
  for (const locale of locales) {
    if (locale.languageCode === 'en' || locale.languageCode === 'es') {
      return locale.languageCode;
    }
  }
  return 'en';
};

/**
 * Converts a locale code to a Transifex-compatible locale code
 * Transifex uses underscore format (e.g., 'es_US') and supports 'en' and 'es_US'
 * @param locale - The locale code from the system (e.g., 'en-US', 'es-US', 'es')
 * @returns A Transifex-compatible locale code
 */
export const getTransifexLocale = (locale: SupportedLanguageCode): string => {
  // Map to supported Transifex locales
  return locale === 'es' ? 'es_US' : 'en';
};

export const getServerLocale = (locale: SupportedLanguageCode): string => {
  switch (locale) {
    case 'es':
      return 'es-US';
    case 'en':
      return 'en-US';
  }
};

export const getZendeskLocale = (locale: SupportedLanguageCode): string => {
  switch (locale) {
    case 'es':
      return 'es';
    case 'en':
      return 'en-us';
  }
};

// Uses getSystemLocale (not the hook) intentionally — Zendesk URLs are resolved
// at call time so pages don't reload or re-launch if the language changes mid-session.
export const getLocalizedZendeskUrl = (path: string): string => {
  const locale = getSystemLocale();
  const zendeskLocale = getZendeskLocale(locale);
  return `https://support.vida.com/hc/${zendeskLocale}/${path}`;
};
